import "./base.css"

import { FieldValues, UseControllerProps, useController } from "react-hook-form"
import React, { useEffect, useRef } from "react"

import WrapWithError from "./wrap-with-error"
import classNames from "classnames"
import { useRequiredMark } from "./required-mark"
import { useState } from "react"
import { useTranslation } from "react-i18next"

type InputProps = {
    transform?: {
        output: (value: string) => string
    }
    length?: number
    showLengthInfo?: boolean
    type: HTMLInputElement["type"]
    autocomplete?: HTMLInputElement["autocomplete"]
    width?: number | string
    height?: number
    autoSize?: boolean
    label?: string
    onChange?: (value: string) => void
    className?: string
    required?: boolean
    hideErrorMessage?: boolean
    placeholder?: string
}

const Input = <T extends FieldValues>({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
    transform,
    length,
    showLengthInfo,
    type,
    autocomplete,
    width,
    height,
    autoSize,
    label,
    onChange: onChangeProp,
    className,
    required,
    hideErrorMessage,
    placeholder,
}: InputProps & UseControllerProps<T>) => {
    const {
        field: { onChange, onBlur, value, ref: controllerRef, ...rest },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
        rules,
        defaultValue,
        shouldUnregister,
    })

    const { t } = useTranslation("shared")

    const showRequiredMark = useRequiredMark()

    const info =
        length && showLengthInfo
            ? {
                  message: t("remaining_chars", { count: length - value.length }),
              }
            : undefined

    const [showPassword, setShowPassword] = useState(false)

    // dirty fix for autofilled inputs from chrome, works only if user interacted with window
    let ref = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        if (!ref.current) return

        const interval = setInterval(() => {
            if (ref.current?.value) {
                onChange(transform ? transform.output(ref.current.value) : ref.current.value)
                clearInterval(interval)
            }
        }, 100)
        return () => clearInterval(interval)
    }, [ref, onChange, transform])

    return (
        <div
            className={classNames("inputBlock", {
                error: error,
                wasInFocus: (isTouched && value) || value,
                // @ts-ignore
                [className]: !!className,
            })}
        >
            <WrapWithError error={error} info={info} hideError={hideErrorMessage} id={name}>
                <input
                    type={type !== "password" ? type : showPassword ? "text" : "password"}
                    id={name}
                    value={value}
                    onBlur={onBlur}
                    onChange={e => {
                        onChange(transform ? transform.output(e.target.value) : e)
                        onChangeProp && onChangeProp(e.target.value)
                    }}
                    className="input"
                    autoComplete={autocomplete}
                    maxLength={length}
                    ref={element => {
                        ref.current = element
                        controllerRef(element)
                    }}
                    style={{ width: width, height: height }}
                    size={autoSize && (length || value?.length)}
                    placeholder={placeholder}
                    required={required}
                    aria-invalid={!!error}
                    aria-errormessage={"err-" + name}
                    {...rest}
                />
                {label && (
                    <label className="label" htmlFor={name}>
                        {label}
                        {required && showRequiredMark && <span className="required"> *</span>}
                    </label>
                )}
                {type === "password" && (
                    <button type="button" onClick={() => setShowPassword(prev => !prev)} className="password">
                        <i className="lavita-icon"></i>
                        {showPassword && <div />}
                    </button>
                )}
            </WrapWithError>
        </div>
    )
}

export default Input
